import React from 'react';
import { Shield, Activity, Crown, Users, Sparkles } from 'lucide-react';
import { UserRole } from '@/hooks/useUserRole';

interface RoleBadgeProps {
  role: UserRole;
  variant?: 'default' | 'compact' | 'icon-only';
  className?: string;
}

export const RoleBadge: React.FC<RoleBadgeProps> = ({ 
  role, 
  variant = 'default',
  className = '' 
}) => {
  if (!role || role === 'user') return null;

  const getRoleConfig = () => {
    switch (role) {
      case 'admin':
        return {
          icon: Crown,
          label: 'Admin',
          colors: 'bg-destructive/10 text-destructive border-destructive/20',
          glowColor: 'shadow-destructive/20'
        };
      case 'moderator':
        return {
          icon: Shield,
          label: 'Moderator',
          colors: 'bg-primary/10 text-primary border-primary/20',
          glowColor: 'shadow-primary/20'
        };
      case 'practitioner':
        return {
          icon: Activity,
          label: 'Practitioner',
          colors: 'bg-accent/10 text-accent border-accent/20',
          glowColor: 'shadow-accent/20'
        };
      case 'partners':
        return {
          icon: Users,
          label: 'Partner',
          colors: 'bg-secondary/10 text-secondary border-secondary/20',
          glowColor: 'shadow-secondary/20'
        };
      default:
        return null;
    }
  };

  const config = getRoleConfig();
  if (!config) return null;

  const { icon: Icon, label, colors, glowColor } = config;

  if (variant === 'icon-only') {
    return (
      <div className={`inline-flex items-center justify-center w-5 h-5 rounded-full border ${colors} ${className}`}>
        <Icon className="h-3 w-3" />
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${colors} hover:shadow-md hover:${glowColor} transition-all duration-200 ${className}`}>
        <Icon className="h-3 w-3" />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium ${colors} hover:shadow-md hover:${glowColor} transition-all duration-200 zen-hover ${className}`}>
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      <Sparkles className="h-3 w-3 opacity-60" />
    </div>
  );
};